import { useTranslation } from "react-i18next";

import { UsageMeterHeader, UsageMeterRow } from "features/usage-config/components/usage-meter-row";
import { USAGE_LIMITS_I18N, USAGE_METER_CATALOG } from "features/usage-config/constants/usage-meters";

import { MeterDraft } from "./usage-config-draft";
import { UsageCardFrame, UsageFieldOrigin } from "./usage-card-frame";

const T = USAGE_LIMITS_I18N;

export interface UsageMetersCardProps {
  meters: Record<string, MeterDraft>;
  enforcementEnabled: boolean;
  blocked?: boolean;
  onMeterChange: (meterKey: string, enabled: boolean) => void;
  hint: string;
  dimmed?: boolean;
  readOnly?: boolean;
  inherited?: boolean;
  onFollowTenant?: () => void;
}

export const UsageMetersCard = ({
  meters,
  enforcementEnabled,
  blocked,
  onMeterChange,
  hint,
  dimmed,
  readOnly,
  inherited,
  onFollowTenant,
}: UsageMetersCardProps) => {
  const { t } = useTranslation();

  return (
    <UsageCardFrame cardId="meters" label={t(T.WHAT_AGENTS_CHECK)} dimmed={dimmed}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <p className="max-w-prose text-xs text-muted-foreground">{hint}</p>
        <UsageFieldOrigin inherited={inherited} onFollowTenant={onFollowTenant} readOnly={readOnly} />
      </div>

      {/* Fixed columns, so a narrow screen scrolls the table rather than stacking the
          captions on top of each other. */}
      <div className="overflow-x-auto">
        <div className="min-w-[46rem] divide-y">
          <UsageMeterHeader hasToggle />
          {USAGE_METER_CATALOG.map((meter) => {
            const enabled = meters[meter.meterKey]?.enabled ?? true;
            const status = blocked
              ? T.BLOCKED
              : !enforcementEnabled
                ? T.NOT_ENFORCED
                : enabled
                  ? T.ENFORCED
                  : T.NO_LIMIT_SKIP_CHECK;
            return (
              <UsageMeterRow
                key={meter.meterKey}
                meter={meter}
                isDimmed={enforcementEnabled && !blocked && !enabled}
                toggle={{
                  checked: enabled,
                  disabled: readOnly || !enforcementEnabled,
                  onChange: (checked) => onMeterChange(meter.meterKey, checked),
                }}
                status={t(status)}
              />
            );
          })}
        </div>
      </div>
    </UsageCardFrame>
  );
};
